import React, { useEffect, useState, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  PhoneInTalk,
  PhoneIphone,
  Paid,
  Dashboard,
  Inventory,
  PointOfSale,
  ShoppingCart,
  TrendingUp,
  Settings,
  NotificationsNone,
  People,
  LocalShipping,
  Science,
  ShoppingCartCheckout,
  Sync,
  Assessment,
  AttachMoney,
  Autorenew,
  AccountCircle,
  Logout,
  Business,
  RestaurantMenu,
  Flag,
  EventNote,
  PrecisionManufacturing,
  Scale,
  PlaylistAddCheck,
  ExpandLess,
  ExpandMore,
  CalendarMonth,
  Link as LinkIcon,
  AccountBalance,
  Insights,
  Receipt,
  Lock,
  Map as PlantMapIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { logout } from '../store/authSlice';
import { Brand } from '../components/Brand';
import { toggleSidebar, setSidebarOpen } from '../store/uiSlice';
import api from '../services/api';
import { hasPageAccess, SELLER_EXTENDED_FLAG } from '../config/rolePermissions';
import SetPasswordOverlay from '../components/SetPasswordOverlay';

const drawerWidth = 280;
// Sidebar claro y consistente con el theme (restyle híbrido frío-limpio).
const sidebarBg = '#FFFFFF';
const sidebarHover = 'rgba(17,17,17,0.04)';
const sidebarActive = 'rgba(56,77,244,0.10)'; // tinte índigo del primary
const sidebarAccent = '#384DF4';               // barrita/icono del ítem activo
const sidebarText = '#0A0A0A';                 // ink
const sidebarMuted = '#51606E';                // slate

// Plan Gratis: solo ventas/POS, productos y stock. El resto se ve con candado y lleva a mejorar el plan.
// Espejo de FreePlan.LockedApiPrefixes del backend (que responde 402 FEATURE_LOCKED).
const FREE_PLAN_LOCKED = new Set([
  'calendar', 'cash-flow', 'expenses', 'opportunities', 'activities', 'prospects', 'sellers',
  'materials', 'services', 'insumos', 'recipes', 'production-orders', 'retention-samples',
  'replenishment', 'purchase-requests', 'purchases', 'debt-ranking', 'suppliers',
  'ai', 'analytics', 'reports', 'purchase-suggestions', 'channels', 'ml-mapping', 'users',
  'today', 'lot-profitability',
  'commercial-dashboard', 'price-lists', 'uninvoiced', 'channel-profitability', 'production-plan', 'commercial-settings', 'import-spreadsheet',
  'factory-production', 'factory-costs', 'factory-outwork', 'factory-customer-orders', 'factory-debts',
  'factory-stock', 'factory-storage', 'factory-maintenance', 'factory-materials',
]);

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  flagKey?: string;
  badge?: number;
  badgeLabel?: string;
  badgeColor?: 'default'|'success'|'error'|'warning';
}

const buildNav = (channelsBadge?: { label: string; color: 'success'|'error'|'default' }): { section: string; items: NavigationItem[] }[] => [
  {
    section: 'Inicio',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
      { id: 'today', label: 'Hoy', icon: <PhoneInTalk />, path: '/today', flagKey: 'commercial-today' },
      { id: 'mobile', label: 'Modo celular', icon: <PhoneIphone />, path: '/m', flagKey: 'mobile-mode' },
      { id: 'calendar', label: 'Calendario', icon: <CalendarMonth />, path: '/calendar' },
      { id: 'cash-flow', label: 'Flujo de Caja', icon: <AccountBalance />, path: '/cash-flow' },
      { id: 'expenses', label: 'Gastos', icon: <Receipt />, path: '/expenses' },
    ],
  },
  {
    section: 'Comercial',
    items: [
      { id: 'pos', label: 'Punto de Venta', icon: <PointOfSale />, path: '/pos' },
      { id: 'caja', label: 'Caja', icon: <AccountBalance />, path: '/caja' },
      { id: 'sales', label: 'Ventas', icon: <PointOfSale />, path: '/sales' },
      { id: 'opportunities', label: 'Oportunidades', icon: <Flag />, path: '/opportunities' },
      { id: 'activities', label: 'Actividades', icon: <EventNote />, path: '/activities' },
      { id: 'customers', label: 'Clientes', icon: <People />, path: '/customers' },
      { id: 'prospects', label: 'Prospectos', icon: <People />, path: '/prospects' },
      { id: 'sellers', label: 'Vendedores', icon: <People />, path: '/sellers' },
    ],
  },
  {
    section: 'Gestión',
    items: [
      { id: 'commercial-dashboard', label: 'Tablero comercial', icon: <Insights />, path: '/commercial-dashboard', flagKey: 'commercial-suite' },
      { id: 'production-plan', label: 'Plan de producción', icon: <PrecisionManufacturing />, path: '/production-plan', flagKey: 'commercial-suite' },
      { id: 'channel-profitability', label: 'Ganancia por canal', icon: <Paid />, path: '/channel-profitability', flagKey: 'commercial-suite' },
      { id: 'uninvoiced', label: 'Facturas pendientes', icon: <Receipt />, path: '/uninvoiced', flagKey: 'commercial-suite' },
      { id: 'price-lists', label: 'Precios y combos', icon: <Assessment />, path: '/price-lists', flagKey: 'commercial-suite' },
      { id: 'commercial-settings', label: 'Costos y objetivos', icon: <AccountBalance />, path: '/commercial-settings', flagKey: 'commercial-suite' },
      { id: 'import-spreadsheet', label: 'Importar planilla', icon: <Sync />, path: '/import-spreadsheet', flagKey: 'commercial-suite' },
    ],
  },
  {
    section: 'Catálogo',
    items: [
      { id: 'products', label: 'Productos', icon: <Inventory />, path: '/products' },
      { id: 'materials', label: 'Materias primas', icon: <Science />, path: '/materials' },
      { id: 'services', label: 'Servicios', icon: <Business />, path: '/services' },
      { id: 'insumos', label: 'Insumos', icon: <Science />, path: '/insumos' },
      { id: 'recipes', label: 'Formulaciones', icon: <RestaurantMenu />, path: '/recipes' },
      { id: 'prices', label: 'Precios', icon: <Assessment />, path: '/prices' },
    ],
  },
  {
    section: 'Operaciones',
    items: [
      { id: 'inventory', label: 'Inventario', icon: <ShoppingCart />, path: '/inventory' },
      { id: 'stock-movements', label: 'Movimientos', icon: <TrendingUp />, path: '/stock-movements' },
      { id: 'production-orders', label: 'Producción', icon: <PrecisionManufacturing />, path: '/production-orders' },
      { id: 'retention-samples', label: 'Muestras de retención', icon: <Science />, path: '/retention-samples' },
      { id: 'replenishment', label: 'Reabastecimiento', icon: <Autorenew />, path: '/replenishment' },
    ],
  },
  {
    // Vertical fábrica (plásticos por inyección, 2026-10)
    section: 'Fábrica',
    items: [
      { id: 'factory-plant', label: 'Planta', icon: <PlantMapIcon />, path: '/factory/plant', flagKey: 'factory-suite' },
      { id: 'factory-stock', label: 'Stock y alertas', icon: <Inventory />, path: '/factory/stock', flagKey: 'factory-suite' },
      { id: 'factory-materials', label: 'Materia prima', icon: <Scale />, path: '/factory/materials', flagKey: 'factory-suite' },
      { id: 'factory-production', label: 'Producción', icon: <PrecisionManufacturing />, path: '/factory/production', flagKey: 'factory-suite' },
      { id: 'factory-product-lots', label: 'Lotes y pallets', icon: <PrecisionManufacturing />, path: '/factory/product-lots', flagKey: 'factory-suite' },
      { id: 'factory-schedule', label: 'Plan de máquinas', icon: <Autorenew />, path: '/factory/schedule', flagKey: 'factory-suite' },
      { id: 'factory-live', label: 'Inyectoras en vivo', icon: <PrecisionManufacturing />, path: '/factory/live', flagKey: 'factory-suite' },
      { id: 'factory-storage', label: 'Depósito', icon: <ShoppingCart />, path: '/factory/storage', flagKey: 'factory-suite' },
      { id: 'factory-maintenance', label: 'Mantenimiento', icon: <Autorenew />, path: '/factory/maintenance', flagKey: 'factory-suite' },
      { id: 'factory-costs', label: 'Costeo', icon: <Assessment />, path: '/factory/costs', flagKey: 'factory-suite' },
      { id: 'factory-outwork', label: 'Armado afuera', icon: <People />, path: '/factory/outwork', flagKey: 'factory-suite' },
      { id: 'factory-customer-orders', label: 'OC de clientes', icon: <LocalShipping />, path: '/factory/customer-orders', flagKey: 'factory-suite' },
      { id: 'factory-debts', label: 'Deudas y planes', icon: <AccountBalance />, path: '/factory/debts', flagKey: 'factory-suite' },
    ],
  },
  {
    section: 'Abastecimiento',
    items: [
      { id: 'purchase-requests', label: 'Pedidos internos', icon: <PlaylistAddCheck />, path: '/purchase-requests' },
      { id: 'purchases', label: 'Compras', icon: <ShoppingCartCheckout />, path: '/purchases' },
      { id: 'debt-ranking', label: 'Orden de pago', icon: <Receipt />, path: '/debt-ranking' },
      { id: 'suppliers', label: 'Proveedores', icon: <LocalShipping />, path: '/suppliers' },
    ],
  },
  {
    section: 'Inteligencia',
    items: [
      { id: 'ai', label: 'Asistente IA', icon: <Insights />, path: '/ai', flagKey: 'ai-invoice-scan' },
      { id: 'analytics', label: 'Análisis', icon: <TrendingUp />, path: '/analytics' },
      { id: 'reports', label: 'Reportes', icon: <Assessment />, path: '/reports' },
      { id: 'profitability', label: 'Rentabilidad', icon: <AttachMoney />, path: '/profitability' },
      { id: 'lot-profitability', label: 'Ganancia por lote', icon: <Paid />, path: '/lot-profitability', flagKey: 'sales-lot-fifo' },
      { id: 'purchase-suggestions', label: 'Sugerencias de Compra', icon: <Insights />, path: '/intelligence/purchase-suggestions' },
    ],
  },
  {
    section: 'Sistema',
    items: [
      { id: 'channels', label: 'Canales', icon: <Sync />, path: '/channels', badgeLabel: channelsBadge?.label, badgeColor: channelsBadge?.color },
      { id: 'ml-mapping', label: 'Mapeo ML', icon: <LinkIcon />, path: '/ml-mapping' },
      { id: 'users', label: 'Usuarios', icon: <People />, path: '/users' },
      { id: 'notification-settings', label: 'Notificaciones', icon: <NotificationsNone />, path: '/notification-settings' },
      { id: 'settings', label: 'Configuración', icon: <Settings />, path: '/settings' },
    ],
  },
];

export const AdminLayout: React.FC = () => {
  const theme = useTheme();
  // Desde md (≥900px) el menú empuja el contenido; por debajo es un overlay que se cierra al navegar.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  
  const { user, tenant } = useSelector((state: RootState) => state.auth);
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);

  // sidebarOpen arranca en true (pensado para el Drawer "persistent" de desktop). En mobile
  // el Drawer es "temporary" (modal a pantalla completa): si arranca abierto, tapa al propio
  // boton de hamburguesa que deberia cerrarlo, y parece que "el menu no anda". Se cierra una
  // sola vez al entrar en mobile, sin pisar despues los toggles manuales del usuario.
  const closedOnMobileMount = useRef(false);
  useEffect(() => {
    if (isMobile && !closedOnMobileMount.current) {
      closedOnMobileMount.current = true;
      dispatch(setSidebarOpen(false));
    }
  }, [isMobile, dispatch]);

  // If the admin hasn't completed the onboarding wizard yet, force them through
  // /completar-perfil before showing the app. Runs inside AdminLayout so it
  // catches both fresh signups and admins who navigate in directly.
  useEffect(() => {
    if (!tenant) return;
    const role = (user?.role || '').toLowerCase();
    if (role !== 'admin') return;
    if (!tenant.onboardingCompletedAt && location.pathname !== '/completar-perfil') {
      navigate('/completar-perfil', { replace: true });
    }
  }, [tenant, user?.role, location.pathname, navigate]);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPath, setSelectedPath] = useState('/dashboard');
  const [navSections, setNavSections] = useState<{ section: string; items: NavigationItem[] }[]>([]);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    buildNav().forEach(g => { initial[g.section] = true; });
    return initial;
  });
  const [featureFlags, setFeatureFlags] = useState<Set<string> | null>(null);
  const [flagsReady, setFlagsReady] = useState(false);
  const [channelsBadge, setChannelsBadge] = useState<{ label: string; color: 'success'|'error'|'default' } | undefined>(undefined);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFreePlan, setIsFreePlan] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await (api as any).api.get('/checkout/status');
        setIsFreePlan(res.data?.status === 'Free');
      } catch {
        setIsFreePlan(false);
      }
    })();
  }, []);

  const isLockedItem = (id: string) => isFreePlan && FREE_PLAN_LOCKED.has(id);

  // Entrar por URL a un módulo con candado lleva a mejorar el plan.
  useEffect(() => {
    if (!isFreePlan) return;
    const locked = buildNav().flatMap(sec => sec.items)
      .find(it => FREE_PLAN_LOCKED.has(it.id) && (location.pathname === it.path || location.pathname.startsWith(`${it.path}/`)));
    if (locked) navigate(`/subscription/upgrade?from=${locked.id}`, { replace: true });
  }, [isFreePlan, location.pathname, navigate]);

  const hasFlag = (key: string) => !!featureFlags?.has(key.toLowerCase());

  // Con "mobile-mode", en el celular el inicio es el modo de botones grandes, salvo que el
  // usuario haya elegido "Versión completa" en esta sesión.
  const isPhone = useMediaQuery('(max-width:700px)');
  useEffect(() => {
    if (!flagsReady || !isPhone || location.pathname !== '/dashboard') return;
    if (!featureFlags?.has('mobile-mode')) return;
    let desktopChosen = false;
    try { desktopChosen = sessionStorage.getItem('df-desktop') === '1'; } catch { /* sin storage */ }
    if (!desktopChosen) navigate('/m', { replace: true });
  }, [flagsReady, featureFlags, isPhone, location.pathname, navigate]);

  // Fetch channels to display connection badge
  useEffect(() => {
    (async () => {
      try {
        const ch = await api.getChannels();
        const total = (ch.items || []).length;
        const connected = (ch.items || []).filter((c: any) => c.isConnected).length;
        const color = total > 0 && connected === total ? 'success' : (connected > 0 ? 'default' : 'error');
        setChannelsBadge({ label: `${connected}/${total}`, color } as any);
      } catch {
        setChannelsBadge(undefined);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const flags = await api.getFeatureFlags();
        const enabled = new Set((flags || []).filter(f => f.enabled).map(f => f.key.toLowerCase()));
        setFeatureFlags(enabled);
      } catch {
        setFeatureFlags(null);
      }
      setFlagsReady(true);
      setInitialLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!flagsReady) {
      setNavSections([]);
      return;
    }
    const base = buildNav(channelsBadge);
    // If the feature-flags fetch failed (featureFlags === null), don't gate by
    // flags — otherwise a transient API failure leaves users with an empty menu.
    const flagsAvailable = featureFlags !== null;
    const allowedFlags = featureFlags ?? new Set<string>();
    const userRole = user?.role || 'Admin';
    const sellerExtended = allowedFlags.has(SELLER_EXTENDED_FLAG);
    const filtered = base
      .map(section => ({
        ...section,
        items: section.items.filter(it => {
          const flagKey = (it.flagKey || it.id).toLowerCase();
          const flagOk = !flagsAvailable || allowedFlags.has(flagKey);
          return flagOk && hasPageAccess(userRole, it.id, sellerExtended);
        })
      }))
      .filter(sec => sec.items.length > 0);
    setNavSections(filtered);
  }, [channelsBadge, featureFlags, flagsReady, user?.role]);

  useEffect(() => {
    setSelectedPath(location.pathname);
    const sectionWithPath = navSections.find(sec => sec.items.some(it => location.pathname.startsWith(it.path)));
    if (sectionWithPath) {
      setOpenSections(prev => ({ ...prev, [sectionWithPath.section]: true }));
    }
  }, [location.pathname, navSections]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      dispatch(setSidebarOpen(!sidebarOpen));
    } else {
      dispatch(toggleSidebar());
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleNavigation = (path: string) => {
    setSelectedPath(path);
    navigate(path);
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  const toggleSectionOpen = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !(prev[section] ?? true) }));
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', visibility: initialLoading ? 'hidden' : 'visible', bgcolor: sidebarBg, color: sidebarText }}>
      {/* Logo Section */}
      <Box sx={{ p: 3, textAlign: 'center', flexShrink: 0 }}>
        <Brand size="md" color={sidebarText} />
        <Typography variant="body2" sx={{ mt: 0.5, color: sidebarMuted }}>
          {tenant?.name}
        </Typography>
        {tenant?.plan && (
          <Chip label={tenant.plan} size="small" variant="outlined" sx={{ mt: 1 }} />
        )}
      </Box>

      <Divider />

      {/* Navigation — sólo esta lista scrollea (logo y usuario quedan fijos). */}
      <List
        sx={{
          flexGrow: 1,
          minHeight: 0,
          px: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Scrollbar fina y discreta para que el scroll vertical no moleste.
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(17,17,17,0.18) transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(17,17,17,0.18)', borderRadius: 3 },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(17,17,17,0.30)' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
        }}
      >
        {navSections.map((group, gi) => (
          <Box key={group.section} sx={{ mb: 1 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => toggleSectionOpen(group.section)} sx={{ px: 2 }}>
                <ListItemText
                  primary={group.section}
                  primaryTypographyProps={{ variant: 'overline', color: sidebarMuted, letterSpacing: '0.08em' }}
                />
                {openSections[group.section] ?? true ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openSections[group.section] ?? true} timeout="auto" unmountOnExit>
              {group.items.map((item) => (
                <ListItem key={item.id} disablePadding sx={{ mb: 0.5, pl: 1 }}>
                  <ListItemButton
                    selected={selectedPath === item.path}
                    onClick={() => handleNavigation(isLockedItem(item.id) ? `/subscription/upgrade?from=${item.id}` : item.path)}
                    sx={{
                      opacity: isLockedItem(item.id) ? 0.5 : 1,
                      borderRadius: 2,
                      position: 'relative',
                      color: sidebarText,
                      '& .MuiListItemIcon-root': { color: sidebarMuted, minWidth: 36 },
                      '&:hover': { backgroundColor: sidebarHover },
                      '&.Mui-selected': {
                        backgroundColor: sidebarActive,
                        color: sidebarText,
                        '&:hover': { backgroundColor: sidebarActive },
                        '& .MuiListItemIcon-root': { color: sidebarAccent },
                        // Barrita de acento a la izquierda (estilo Cobrando)
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 6,
                          bottom: 6,
                          width: 3,
                          borderRadius: 3,
                          backgroundColor: sidebarAccent,
                        },
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: selectedPath === item.path ? 600 : 400 }} />
                    {isLockedItem(item.id) && <Lock sx={{ fontSize: 16, color: sidebarMuted }} />}
                    {!isLockedItem(item.id) && (item.badgeLabel || item.badge) && (
                      <Chip label={item.badgeLabel || item.badge} size="small" color={item.badgeColor || 'default'} variant={item.badgeColor ? 'filled' : 'outlined'} />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </Collapse>
          </Box>
        ))}
      </List>

      <Divider />

      {/* User Menu */}
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <ListItemButton onClick={handleProfileMenuOpen} sx={{ borderRadius: 2 }}>
          <ListItemIcon>
            <Avatar>{user?.name?.[0] || '?'}</Avatar>
          </ListItemIcon>
          <ListItemText primary={user?.name || 'Usuario'} secondary={user?.email} />
        </ListItemButton>
      </Box>
    </Box>
  );

  if (initialLoading) {
    return <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }} />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <SetPasswordOverlay />
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {/* Public trace hint */}
          {hasFlag('public-traceability') && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 2 }}>
              <Typography variant="body2" color="inherit">
                Trazabilidad pública: <code>/trace/{"{ID_LOTE}"}</code>
              </Typography>
            </Box>
          )}

          {/* Tenant Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Business sx={{ mr: 1 }} />
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {tenant?.name}
            </Typography>
          </Box>

          {/* User Menu */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar
              sx={{ width: 32, height: 32 }}
              src={user?.profilePictureUrl}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        id="primary-search-account-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">{user?.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleNavigation('/profile')}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Configurar Perfil
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Cerrar Sesión
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          // Cerrado, el hueco del menú también colapsa (antes quedaban 280px en blanco).
          width: { md: sidebarOpen ? drawerWidth : 0 },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: sidebarOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: sidebarBg,
              color: sidebarText,
              borderRight: '1px solid rgba(17,17,17,0.07)',
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: sidebarBg,
              color: sidebarText,
              borderRight: '1px solid rgba(17,17,17,0.07)',
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          maxWidth: '100vw',
          height: '100vh',
          overflowX: 'hidden',
          overflowY: 'auto',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        {/* Plan Gratis: aviso discreto con salida a mejorar el plan. */}
        {isFreePlan && !location.pathname.startsWith('/subscription') && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap',
              px: 2, py: 0.75, bgcolor: sidebarActive, color: sidebarText, fontSize: '0.85rem',
            }}
          >
            <span>Estás en el plan Gratis: hasta 100 productos y 1 usuario.</span>
            <Box
              component="button"
              type="button"
              onClick={() => navigate('/subscription/upgrade')}
              sx={{
                border: 0, background: 'none', p: 0, cursor: 'pointer', font: 'inherit',
                fontWeight: 600, color: sidebarAccent, textDecoration: 'underline',
              }}
            >
              Mejorar plan
            </Box>
          </Box>
        )}
        {/* Sin padding acá: cada página aporta su propio p:3 (evita el doble margen). */}
        <Box>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
